import React, { type ReactNode, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'

import cs from 'classnames'
import { IconAction } from 'ui-common'
import './style.sass'

const keyCodes = {
	ESC: 27,
}
type ModalProps = {
	children: ReactNode
	right?: boolean
	wide?: boolean
	transparentBackdrop?: boolean
	open: boolean
	handleClose: () => void
	className?: string
	containerClassName?: string
	containerStyle?: React.CSSProperties
}
export const Modal = ({
	children,
	right,
	wide,
	transparentBackdrop,
	open,
	handleClose,
	className,
	containerClassName,
	containerStyle,
}: ModalProps): React.ReactNode => {
	const contentClassName = cs(
		'Modal__content',
		{
			'Modal__content--rightAlign': right,
			'Modal__content--wide': wide,
		},
		className,
	)

	const activeElement = useRef<HTMLElement | null>(null)

	useEffect(() => {
		document.addEventListener('keydown', checkKeyDown)
		function checkKeyDown(e: KeyboardEvent) {
			if (open) {
				if (e.keyCode === keyCodes.ESC) {
					handleClose()
				}
			}
		}
		return () => document.removeEventListener('keydown', checkKeyDown)
	}, [open, handleClose])

	useEffect(() => {
		if (open) {
			activeElement.current = document.activeElement as HTMLElement | null
		} else {
			activeElement.current?.focus()
		}
	}, [open])
	return (
		<AnimatePresence>
			{open ? (
				<div
					className={cs(
						'Modal',
						{
							'Modal--transparentBackdrop': transparentBackdrop,
						},
						containerClassName,
					)}
					id='ModalContainer'
					style={containerStyle}>
					<motion.div
						className={contentClassName}
						tabIndex={0}
						id='Modal__Content'
						initial={{ y: 300, opacity: 0 }}
						animate={{ y: 0, opacity: 1, transition: { ease: [0.43, 0.13, 0.23, 0.96] } }}
						exit={{ y: 300, opacity: 0 }}>
						<div
							role='dialog'
							className='dialog'
							aria-labelledby='Dialog__Title'
							aria-describedby='Modal__Content'
							aria-modal='true'>
							{children}
						</div>
					</motion.div>
				</div>
			) : null}
		</AnimatePresence>
	)
}

export const ModalText = ({ children }: { children: ReactNode }): React.ReactNode => <div className='Modal__text'>{children}</div>
export const ModalTitle = ({ children, className = '' }: { children: ReactNode; className?: string }): React.ReactNode => (
	<h1 className={cs('Modal__title', className)} id='Dialog__Title'>
		{children}
	</h1>
)

export const ModalHeader = ({ close, title }: { close: () => void; title: ReactNode }): React.ReactNode => {
	const { t } = useTranslation(['ui-common/modal'])
	return (
		<>
			<div className='Header__Container'>
				<h1 className='Header__Container__Title' id='Dialog__Title'>
					{title}
				</h1>
				<IconAction
					onClick={close}
					name='close'
					aria-label={t('closeModal')}
					className='Header__Container__Icon'></IconAction>
			</div>
			<hr className='Header__Container__divider' />
		</>
	)
}
